// Layer: Page (UI) – chỉ render JSX, gọi hook domain từ features.
import React, { useMemo } from 'react';
import { Table, Space, Tag, Button, Popconfirm, Spin } from 'antd';
import { Pencil, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import type { DepartmentDetail } from '../types/department';
import { Pagination } from '../components/common/Pagination';
import { FilterSection } from '../components/common/FilterSection';
import { AddDepartmentModal } from '../components/department/AddDepartmentModal';
import { EditDepartmentModal } from '../components/department/EditDepartmentModal';
import { savePersonnelFilterState } from '@/features/personnel/personnelFilterStorage';
import { useDepartmentManagement } from '@/features/departments/useDepartmentManagement';
import badgePlus from '../assets/badge-plus.svg';
import styles from './DepartmentManagementPage.module.css';
import { useAuth } from '@/contexts/AuthContext';
import { can } from '@shared/utils';

export const DepartmentManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canCreateDepartment = can(user, 'department', 'create');
  const canUpdateDepartment = can(user, 'department', 'update');
  const canDeleteDepartment = can(user, 'department', 'delete');
  const canListPersonnel = can(user, 'user', 'list');

  const {
    // filters
    searchInput,
    setSearchInput,
    statusFilter,
    setStatusFilter,

    // data
    departmentData,
    loading,
    total,

    // pagination
    currentPage,
    pageSize,
    handlePageChange,
    handlePageSizeChange,

    // modals & selection
    isAddModalOpen,
    isEditModalOpen,
    selectedDepartmentItem,
    handleAddDepartment,
    handleEditDepartment,
    handleCloseAddModal,
    handleCloseEditModal,

    // actions
    deletingId,
    handleDeleteDepartment,
    handleSubmitAddDepartment,
    handleSubmitEditDepartment,
    handleClearFilter,
    handleSearch,
  } = useDepartmentManagement();

  /** Render tag trạng thái phòng ban */
  const getStatusTag = (status: 'active' | 'inactive') => {
    if (status === 'active') {
      return (
        <Tag className={styles.statusTagActive}>
          Hoạt động
        </Tag>
      );
    }
    return (
      <Tag className={styles.statusTagInactive}>
        Không hoạt động
      </Tag>
    );
  };

  const handlePersonnelClick = (record: DepartmentDetail) => {
    // Lưu filter state với phòng ban được chọn và điều hướng đến trang nhân sự
    savePersonnelFilterState({
      searchText: '',
      selectedDepartment: record.name, // Sử dụng tên phòng ban để filter
      currentPage: 1,
      pageSize: 10,
    });
    navigate('/management/personnel');
  };

  /** Columns được memo để tránh re-render nặng khi state thay đổi không liên quan */
  const columns: ColumnsType<DepartmentDetail> = useMemo(
    () => [
      { title: 'Mã', dataIndex: 'code', key: 'code', width: 100 },
      { title: 'Tên phòng ban', dataIndex: 'name', key: 'name', width: 150 },
      {
        title: 'Nhân sự',
        dataIndex: 'personnelCount',
        key: 'personnelCount',
        width: 120,
        render: (count: number, record: DepartmentDetail) => (
          canListPersonnel ? (
            <Button type="link" className={styles.personnelLink} onClick={() => handlePersonnelClick(record)}>
              {count} nhân sự
            </Button>
          ) : (
            <span>{count} nhân sự</span>
          )
        ),
      },
      {
        title: 'Trạng thái',
        dataIndex: 'status',
        key: 'status',
        width: 120,
        render: (status: 'active' | 'inactive') => getStatusTag(status),
      },
      ...(canUpdateDepartment || canDeleteDepartment
        ? ([{
        title: 'Thao tác',
        key: 'action',
        width: 120,
        align: 'center',
        render: (_: unknown, record: DepartmentDetail) => (
          <Space size={0}>
            {canUpdateDepartment && <Button
              type="text"
              icon={<Pencil size={16} />}
              onClick={() => handleEditDepartment(record)}
              className={styles.actionButton}
            />}
            {canDeleteDepartment && <Popconfirm
              title={null}
              icon={null}
              description="Bạn có chắc chắn muốn xóa phòng ban này?"
              onConfirm={() => handleDeleteDepartment(record)}
              okText="Xóa"
              cancelText="Hủy"
              okButtonProps={{
                danger: true,
                loading: deletingId === record.id,
              }}
              placement="left"
              overlayClassName={styles.popconfirmOverlay}
            >
              <Button type="text" danger icon={<Trash2 size={16} />} className={styles.actionButton} />
            </Popconfirm>}
          </Space>
        ),
      }] satisfies ColumnsType<DepartmentDetail>)
        : []),
    ],
    [canDeleteDepartment, canListPersonnel, canUpdateDepartment, deletingId]
  );

  return (
    <div className={styles.container}>
      <FilterSection
        title="Bộ lọc"
        subtitle="Tìm kiếm và lọc dữ liệu"
        searchPlaceholder="Tìm kiếm theo mã phòng ban, tên phòng ban"
        searchValue={searchInput}
        onSearchChange={setSearchInput}
        onSearch={handleSearch}
        showDepartmentFilter={false}
        showStatusFilter={true}
        selectedStatus={statusFilter}
        onStatusChange={(value) => {
          setStatusFilter(value);
          handlePageChange(1);
        }}
        onClearFilter={handleClearFilter}
        extraActions={canCreateDepartment ? (
          <Button
            type="primary"
            icon={<img src={badgePlus} alt="add" className={styles.addIcon} />}
            size="large"
            onClick={handleAddDepartment}
            className={styles.addButton}
          >
            Thêm phòng ban
          </Button>
        ) : undefined}
      />

      <div className={styles.tableContainer}>
        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={departmentData}
            rowKey="id"
            pagination={false}
            className={styles.table}
          />
        </Spin>
        <Pagination
          current={currentPage}
          total={total}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          showSizeChanger={true}
          showTotal={true}
          itemName="phòng ban"
        />
      </div>

      <AddDepartmentModal
        open={isAddModalOpen}
        onClose={handleCloseAddModal}
        onSubmit={handleSubmitAddDepartment}
      />

      <EditDepartmentModal
        open={isEditModalOpen}
        onClose={handleCloseEditModal}
        onSubmit={handleSubmitEditDepartment}
        initialData={selectedDepartmentItem || undefined}
      />
    </div>
  );
};

